import React, {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { Check, Copy } from '../icons'
import { classes } from '../utils'

export type TableCellProps = ComponentPropsWithoutRef<'td'> & {
  isActive?: boolean
  activeOnHover?: boolean
  enableCopy?: boolean
}

export function TableCell({
  className,
  activeOnHover,
  enableCopy,
  children,
  ...props
}: TableCellProps) {
  const [copied, setCopied] = useState(false)

  const handleCopyClick = useCallback((e: React.MouseEvent) => {
    // Stop event propagation to prevent row click
    e.stopPropagation()

    const content = e.currentTarget.closest('td')?.textContent
    if (content) {
      navigator.clipboard
        .writeText(content)
        .catch((err) => console.error('Failed to copy text: ', err))
      setCopied(true)
    }
  }, [])

  useEffect(() => {
    if (!copied) return

    const timeoutId = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timeoutId)
  }, [copied])

  return (
    <td
      className={classes(
        'group relative border-b border-b-border-normal px-1.5',
        {
          'cursor-pointer': activeOnHover,
        },
        className,
      )}
      {...props}
    >
      {children}

      {enableCopy && (
        <div
          data-testid="copy-cell"
          className="absolute bottom-0 right-0 top-0 z-10 w-8 p-1 opacity-0 transition-opacity group-hover:bg-background-surface group-hover:opacity-100"
        >
          <button
            type="button"
            className="absolute right-1 top-1/2 z-20 -translate-y-1/2 rounded-md bg-background-surface-hovered p-1"
            onClick={handleCopyClick}
            title="Copy to clipboard"
            aria-label="Copy to clipboard"
            data-testid="copy-cell-button"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      )}
    </td>
  )
}
